import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { vocabAPI } from '../../api/client'
import AppLayout from '../../components/layout/AppLayout'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

/** Vocabulary Deck Management */
export default function VocabDeckPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')

  const { data: stats } = useQuery({ queryKey: ['vocab-dashboard'], queryFn: vocabAPI.dashboard })
  const { data: allWords = [], isLoading } = useQuery({ queryKey: ['vocab-all'], queryFn: vocabAPI.all })
  const { data: methods } = useQuery({ queryKey: ['vocab-methods'], queryFn: vocabAPI.methods })
  const fsrs = methods?.fsrs ?? false

  const filtered = (allWords as any[]).filter((w: any) =>
    !search || w.word?.toLowerCase().includes(search.toLowerCase()) || w.contextSentence?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <AppLayout>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span className="material-symbols-outlined" style={{ color: '#8e00d5', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>style</span>
            <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8e00d5' }}>Bộ thẻ từ vựng</span>
          </div>
          <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
            Quản lý <span style={{ color: '#8e00d5', fontStyle: 'italic' }}>từ vựng</span>
          </H>
          <p style={{ color: '#535b71', fontSize: '1rem', lineHeight: 1.6 }}>
            Tất cả từ vựng bạn đang học theo hệ thống {fsrs ? 'FSRS' : 'SM-2'}.
          </p>
        </div>
        <button
          onClick={() => navigate('/vocab')}
          style={{ padding: '0.875rem 1.5rem', borderRadius: '9999px', border: 'none', background: 'linear-gradient(135deg, #8e00d5, #eac4ff)', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>play_arrow</span>
          Bắt đầu ôn tập
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(8rem, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          {[
            { label: 'Tổng từ', value: stats.total, color: '#004be2' },
            { label: 'Đã thành thạo', value: stats.mastered, color: '#8e00d5' },
            { label: 'Đang học', value: (stats.total ?? 0) - (stats.mastered ?? 0), color: '#b45309' },
            { label: 'Cần ôn hôm nay', value: stats.dueToday, color: '#b41340' },
          ].map(item => (
            <div key={item.label} style={{ background: 'white', borderRadius: '1.25rem', padding: '1.25rem', textAlign: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
              <H style={{ display: 'block', fontSize: '2rem', fontWeight: 900, color: item.color }}>{item.value ?? 0}</H>
              <span style={{ fontSize: '0.75rem', color: '#535b71', fontWeight: 600 }}>{item.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
        <span className="material-symbols-outlined" style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#a5adc6', fontSize: '1.25rem' }}>search</span>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Tìm từ vựng..."
          style={{ width: '100%', padding: '0.875rem 1rem 0.875rem 3rem', borderRadius: '9999px', background: 'white', border: 'none', outline: 'none', fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}
        />
      </div>

      {/* Word list */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <div className="spinner" />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', background: 'white', borderRadius: '1.5rem' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: '#a5adc6', display: 'block', marginBottom: '1rem' }}>search_off</span>
          <H style={{ display: 'block', fontWeight: 700, color: '#535b71' }}>Không tìm thấy từ nào</H>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(18rem, 1fr))', gap: '1rem' }}>
          {filtered.map((w: any) => (
            <div
              key={w.id}
              style={{ background: 'white', borderRadius: '1.25rem', padding: '1.25rem', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', transition: 'all 0.2s', cursor: 'default' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(142,0,213,0.1)' }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.04)' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.625rem' }}>
                <H style={{ fontWeight: 800, fontSize: '1.0625rem', color: '#8e00d5' }}>{w.word}</H>
                <span style={{
                  padding: '0.2rem 0.625rem', borderRadius: '9999px', fontSize: '0.625rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
                  background: w.repetitions >= 5 ? 'rgba(142,0,213,0.1)' : 'rgba(0,75,226,0.08)',
                  color: w.repetitions >= 5 ? '#8e00d5' : '#004be2',
                }}>
                  {w.repetitions >= 5 ? 'Thành thạo' : `Lần ${w.repetitions ?? 0}`}
                </span>
              </div>
              {w.contextSentence && (
                <p style={{ fontSize: '0.875rem', color: '#535b71', fontStyle: 'italic', marginBottom: '0.5rem', lineHeight: 1.5 }}>
                  "{w.contextSentence}"
                </p>
              )}
              <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                {w.nextReviewDate ? (
                  <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: '#a5adc6', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '0.875rem' }}>schedule</span>
                    Ôn tiếp: {new Date(w.nextReviewDate).toLocaleDateString('vi-VN')}
                  </div>
                ) : <span />}
                {/* FSRS predicted recall probability today */}
                {typeof w.retention === 'number' && (
                  <span
                    title="Xác suất bạn còn nhớ từ này hôm nay (FSRS)"
                    style={{
                      fontSize: '0.6875rem', fontWeight: 800, padding: '0.15rem 0.5rem', borderRadius: '9999px',
                      background: w.retention >= 80 ? 'rgba(15,157,88,0.1)' : w.retention >= 50 ? 'rgba(180,83,9,0.1)' : 'rgba(180,19,64,0.1)',
                      color: w.retention >= 80 ? '#0f9d58' : w.retention >= 50 ? '#b45309' : '#b41340',
                    }}
                  >
                    Ghi nhớ ~{w.retention}%
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  )
}
